import { useEffect } from 'react';
import { create } from 'zustand';
import useUserStore from 'common/src/stores/useUserStore';
import { inboxRequest } from 'common/src/api/settlement-inbox';
import { canAccessSettlementInbox } from 'common/src/utils/inbox-access';
import {
  refreshInboxSummary,
  type InboxSummary,
  type InboxSummaryEntry,
} from 'common/src/utils/inbox-summary';

const useSummaryStore = create<{ entries: Record<string, InboxSummaryEntry> }>(
  () => ({ entries: {} })
);
const requests = new Map<string, Promise<void>>();

async function refresh(key: string, force = false) {
  if (requests.has(key)) return requests.get(key);
  const previous = useSummaryStore.getState().entries[key];
  if (!force && previous && Date.now() - previous.checkedAt < 5_000) return;
  const request = (async () => {
    const entry = await refreshInboxSummary(previous, () =>
      inboxRequest<InboxSummary>('summary')
    );
    useSummaryStore.setState((state) => ({
      entries: {
        ...state.entries,
        [key]: entry,
      },
    }));
  })();
  requests.set(key, request);
  try {
    await request;
  } finally {
    requests.delete(key);
  }
}

export default function useSettlementInboxAccess() {
  const profile = useUserStore((state) => state.userProfile);
  const allowed = canAccessSettlementInbox(profile);
  const key = allowed
    ? `${profile?.auth_id}:${profile?.exporter_id}:${profile?.roles.join(',')}`
    : '';
  const entry = useSummaryStore((state) => state.entries[key]);
  useEffect(() => {
    if (!key) return;
    void refresh(key);
    const update = () => {
      if (!document.hidden) void refresh(key);
    };
    const invalidate = () => {
      void refresh(key, true);
    };
    const timer = window.setInterval(update, 30_000);
    window.addEventListener('focus', update);
    window.addEventListener('settlement-inbox-changed', invalidate);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', update);
      window.removeEventListener('settlement-inbox-changed', invalidate);
    };
  }, [key]);
  return {
    enabled: allowed && Boolean(entry?.enabled),
    pending: allowed ? entry?.pending || 0 : 0,
    loading: allowed && !entry,
    failed: allowed && Boolean(entry?.failed),
    refresh: () => (key ? refresh(key, true) : Promise.resolve()),
  };
}
